import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Chip,
  FormControl,
  InputAdornment,
  InputBase,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  TablePagination,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import ReceiptLongRoundedIcon from "@mui/icons-material/ReceiptLongRounded";
import SearchIcon from "@mui/icons-material/Search";

import {
  getLaunchpadPurchasesAPI,
  LaunchpadPurchase,
  LaunchpadPurchaseFilters,
  LaunchpadPurchaseStatus,
  LaunchpadPurchaseSummary,
} from "../api/transactionNftAPIs";
import {
  columnsLaunchpadPurchase,
  LAUNCHPAD_PURCHASE_STATUSES,
  STATUS_COLORS,
} from "../schema/transactionNft";
import { useDebounce } from "../hooks/customHook/useDebounce";
import { accountingFormat } from "../utils/converText";
import { REPORT_PALETTE as PALETTE } from "../utils/palette.token";

const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

const EMPTY_SUMMARY: LaunchpadPurchaseSummary = {
  count: 0,
  byStatus: {},
  byCurrency: {},
};

const darkInputSx = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: PALETTE.inputBg,
    color: PALETTE.textPrimary,
    "& fieldset": { borderColor: PALETTE.border },
    "&:hover fieldset": { borderColor: PALETTE.accent },
    "&.Mui-focused fieldset": { borderColor: PALETTE.accent },
  },
  "& .MuiInputLabel-root": { color: PALETTE.muted },
  "& .MuiInputLabel-root.Mui-focused": { color: PALETTE.accent },
  "& .MuiInputAdornment-root svg": { color: PALETTE.muted },
  "& .MuiSvgIcon-root": { color: PALETTE.muted },
};

const dateInputSx = (filled: boolean) => ({
  width: 124,
  color: filled ? PALETTE.textPrimary : "rgba(255,255,255,0.4)",
  colorScheme: "dark",
  fontSize: "0.875rem",
  "& input": {
    p: 0,
    "&::-webkit-calendar-picker-indicator": {
      cursor: "pointer",
      opacity: 0.6,
      "&:hover": { opacity: 1 },
    },
  },
});

type CurrencyAmount = { currency: string; value: number };

const StatTile = ({
  label,
  amounts,
  accent,
  hint,
}: {
  label: string;
  amounts: CurrencyAmount[];
  accent?: boolean;
  hint?: string;
}) => (
  <Box
    sx={{
      flex: "1 1 200px",
      minWidth: 180,
      px: 2.5,
      py: 2,
      borderRadius: 2,
      bgcolor: accent ? PALETTE.accentDim : "rgba(255,255,255,0.03)",
      border: `1px solid ${accent ? PALETTE.accent : PALETTE.border}`,
    }}
  >
    <Typography
      sx={{
        fontSize: "0.68rem",
        letterSpacing: "0.1em",
        textTransform: "uppercase",
        color: PALETTE.muted,
        fontWeight: 700,
      }}
    >
      {label}
    </Typography>

    {amounts.length === 0 ? (
      <Typography sx={{ mt: 0.75, fontSize: "1.35rem", fontWeight: 700, color: PALETTE.muted }}>
        —
      </Typography>
    ) : (
      <Stack sx={{ mt: 0.75 }} spacing={0.25}>
        {amounts.map(({ currency, value }) => (
          <Stack key={currency} direction="row" alignItems="baseline" spacing={0.75}>
            <Typography
              sx={{
                fontSize: amounts.length > 1 ? "1.05rem" : "1.35rem",
                fontWeight: 700,
                color: accent ? PALETTE.accent : PALETTE.textPrimary,
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {accountingFormat(value)}
            </Typography>
            <Typography sx={{ fontSize: "0.72rem", color: PALETTE.muted, fontWeight: 600 }}>
              {currency}
            </Typography>
          </Stack>
        ))}
      </Stack>
    )}

    {hint && (
      <Typography sx={{ mt: 0.5, fontSize: "0.7rem", color: PALETTE.muted }}>{hint}</Typography>
    )}
  </Box>
);

const TableSkeleton = ({ rows }: { rows: number }) => (
  <Box sx={{ px: 1 }}>
    {Array.from({ length: rows }).map((_, index) => (
      <Stack
        key={index}
        direction="row"
        spacing={2}
        alignItems="center"
        sx={{ py: 1.5, borderBottom: `1px solid ${PALETTE.border}` }}
      >
        {[130, 200, 190, 120, 130, 130, 120, 100, 140].map((width, cell) => (
          <Skeleton
            key={cell}
            variant="rectangular"
            height={18}
            width={width}
            sx={{ bgcolor: PALETTE.skeletonBase, borderRadius: 1 }}
          />
        ))}
      </Stack>
    ))}
  </Box>
);

const EmptyState = ({ filtered }: { filtered: boolean }) => (
  <Stack alignItems="center" spacing={1} sx={{ py: 8 }}>
    <ReceiptLongRoundedIcon sx={{ fontSize: 44, color: PALETTE.muted, opacity: 0.5 }} />
    <Typography sx={{ color: PALETTE.textSecondary, fontWeight: 600 }}>
      No launchpad purchases found
    </Typography>
    {filtered && (
      <Typography sx={{ color: PALETTE.muted, fontSize: "0.85rem" }}>
        Try widening the date range or clearing the filters.
      </Typography>
    )}
  </Stack>
);

export const TransactionNft = () => {
  const [rows, setRows] = useState<LaunchpadPurchase[]>([]);
  const [summary, setSummary] = useState<LaunchpadPurchaseSummary>(EMPTY_SUMMARY);
  const [total, setTotal] = useState(0);
  const [truncated, setTruncated] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [statusFilter, setStatusFilter] = useState<LaunchpadPurchaseStatus | "">("");
  const [searchInput, setSearchInput] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  const debouncedSearch = useDebounce(searchInput, 400);

  const hasFilters = Boolean(
    statusFilter || dateFrom || dateTo || debouncedSearch.trim()
  );

  const fetchPurchases = useCallback(async () => {
    setLoading(true);
    try {
      const filters: LaunchpadPurchaseFilters = {
        page: page + 1,
        limit: rowsPerPage,
        ...(statusFilter && { status: statusFilter }),
        ...(dateFrom && { from: dateFrom }),
        ...(dateTo && { to: dateTo }),
        ...(debouncedSearch.trim() && { search: debouncedSearch.trim() }),
      };
      const res = await getLaunchpadPurchasesAPI(filters);
      setRows(res.data);
      setSummary(res.summary);
      setTotal(res.pagination.total);
      setTruncated(Boolean(res.truncated));
      setError(null);
    } catch (err: any) {
      setRows([]);
      setSummary(EMPTY_SUMMARY);
      setTotal(0);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to load launchpad transactions"
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter, dateFrom, dateTo, debouncedSearch]);

  useEffect(() => {
    fetchPurchases();
  }, [fetchPurchases]);

  useEffect(() => {
    setPage(0);
  }, [debouncedSearch, statusFilter, dateFrom, dateTo]);

  const currencies = useMemo(
    () =>
      Object.entries(summary.byCurrency).sort(
        (a, b) => b[1].totalAmount - a[1].totalAmount
      ),
    [summary]
  );

  const columns = useMemo(() => columnsLaunchpadPurchase(), []);

  const statusHint = Object.entries(summary.byStatus)
    .map(([status, count]) => `${count} ${status}`)
    .join(" · ");

  return (
    <Box
      sx={{
        bgcolor: PALETTE.cardBg,
        borderRadius: 3,
        overflow: "hidden",
        border: `1px solid ${PALETTE.border}`,
        boxShadow: "0 4px 24px rgba(0,0,0,0.35)",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, md: 4 },
          pt: { xs: 2.5, md: 3.5 },
          pb: 2.5,
          bgcolor: PALETTE.headerBg,
          borderBottom: `1px solid ${PALETTE.border}`,
        }}
      >
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 3 }}>
          <Box
            sx={{
              width: 4,
              height: 28,
              borderRadius: 1,
              bgcolor: PALETTE.accent,
              flexShrink: 0,
            }}
          />
          <ReceiptLongRoundedIcon sx={{ color: PALETTE.accent, fontSize: 24 }} />
          <Typography
            variant="h4"
            sx={{
              fontWeight: "bold",
              color: PALETTE.textPrimary,
              lineHeight: 1.2,
              fontSize: "1.4rem",
            }}
          >
            Launchpad Transactions
          </Typography>
          <Chip
            label={total}
            size="small"
            sx={{
              bgcolor: "rgba(110,200,240,0.12)",
              color: PALETTE.accent,
              fontWeight: 700,
              ml: 1,
            }}
          />
        </Stack>

        <Stack direction="row" spacing={1.5} flexWrap="wrap" useFlexGap sx={{ mb: 3 }}>
          <StatTile
            label="Total volume"
            accent
            amounts={currencies.map(([currency, t]) => ({
              currency,
              value: t.totalAmount,
            }))}
          />
          <StatTile
            label="Seller payout (90%)"
            amounts={currencies.map(([currency, t]) => ({
              currency,
              value: t.totalSellerAmount,
            }))}
          />
          <StatTile
            label="Platform revenue (10%)"
            amounts={currencies.map(([currency, t]) => ({
              currency,
              value: t.totalPlatformFee,
            }))}
          />
          <StatTile
            label="Transactions"
            amounts={
              summary.count
                ? [{ currency: "purchases", value: summary.count }]
                : []
            }
            hint={statusHint || undefined}
          />
        </Stack>

        <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} flexWrap="wrap" useFlexGap>
          <TextField
            placeholder="Search tx hash, wallet, launchpad or email..."
            size="small"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ ...darkInputSx, minWidth: 280 }}
          />

          <FormControl size="small" sx={{ minWidth: 150, ...darkInputSx }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={statusFilter}
              label="Status"
              onChange={(e) =>
                setStatusFilter(e.target.value as LaunchpadPurchaseStatus | "")
              }
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: PALETTE.cardBg,
                    border: `1px solid ${PALETTE.borderStrong}`,
                    "& .MuiMenuItem-root": {
                      color: PALETTE.textPrimary,
                      "&:hover": { bgcolor: PALETTE.accentHover },
                    },
                  },
                },
              }}
            >
              <MenuItem value="">All</MenuItem>
              {LAUNCHPAD_PURCHASE_STATUSES.map((status) => (
                <MenuItem key={status} value={status}>
                  <Chip
                    label={status}
                    size="small"
                    sx={{
                      bgcolor: STATUS_COLORS[status]?.bg,
                      color: STATUS_COLORS[status]?.color,
                      fontWeight: 600,
                      fontSize: "0.68rem",
                      height: 22,
                      textTransform: "capitalize",
                    }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              px: 1.5,
              height: 40,
              borderRadius: "4px",
              border: `1px solid ${PALETTE.border}`,
              bgcolor: PALETTE.inputBg,
              transition: "border-color 0.2s",
              "&:hover, &:focus-within": { borderColor: PALETTE.accent },
            }}
          >
            <Typography sx={{ color: PALETTE.muted, fontSize: "0.875rem" }}>From</Typography>
            <InputBase
              type="date"
              inputProps={{ "aria-label": "Filter from date" }}
              value={dateFrom}
              onChange={(e) => setDateFrom(e.target.value)}
              sx={dateInputSx(Boolean(dateFrom))}
            />
            <Typography sx={{ color: PALETTE.muted, fontWeight: 300 }}>—</Typography>
            <InputBase
              type="date"
              inputProps={{ "aria-label": "Filter to date" }}
              value={dateTo}
              onChange={(e) => setDateTo(e.target.value)}
              sx={dateInputSx(Boolean(dateTo))}
            />
          </Box>
        </Stack>
      </Box>

      <Box sx={{ px: { xs: 1, md: 2 }, pb: 2 }}>
        {truncated && (
          <Alert
            severity="warning"
            sx={{ mt: 2, bgcolor: "rgba(232,200,106,0.12)", color: PALETTE.textPrimary }}
          >
            Showing the most recent results only — totals below cover the loaded
            rows, not every matching transaction. Narrow the date range for exact
            figures.
          </Alert>
        )}

        {loading ? (
          <TableSkeleton rows={Math.min(rowsPerPage, 10)} />
        ) : rows.length === 0 ? (
          <EmptyState filtered={hasFilters} />
        ) : (
          <DataGrid
            rows={rows}
            columns={columns}
            getRowId={(row) => row.transactionId}
            hideFooter
            disableColumnMenu
            disableSelectionOnClick
            autoHeight
            rowHeight={62}
            sx={{
              border: "none",
              color: PALETTE.textPrimary,
              "& .MuiDataGrid-columnHeaders": {
                bgcolor: "rgba(0,0,0,0.2)",
                borderBottom: "1px solid rgba(255,255,255,0.1)",
                borderRadius: 0,
                "& .MuiDataGrid-columnHeaderTitle": {
                  fontWeight: 700,
                  fontSize: "0.68rem",
                  letterSpacing: "0.1em",
                  textTransform: "uppercase",
                  color: PALETTE.muted,
                },
                "& .MuiDataGrid-columnSeparator": { display: "none" },
              },
              "& .MuiDataGrid-row": {
                borderBottom: "1px solid rgba(255,255,255,0.05)",
                transition: "background-color 0.15s ease",
                "&:hover": { bgcolor: PALETTE.rowHover },
                "&:last-of-type": { borderBottom: "none" },
              },
              "& .MuiDataGrid-row:nth-of-type(even)": {
                bgcolor: PALETTE.rowStripe,
                "&:hover": { bgcolor: PALETTE.rowHover },
              },
              "& .MuiDataGrid-cell": {
                borderBottom: "none",
                color: PALETTE.textPrimary,
                fontSize: "0.875rem",
                "&:focus, &:focus-within": { outline: "none" },
              },
              "& .MuiDataGrid-footerContainer": { display: "none" },
            }}
          />
        )}

        <TablePagination
          component="div"
          count={total}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={PAGE_SIZE_OPTIONS}
          sx={{
            color: PALETTE.muted,
            borderTop: "1px solid rgba(255,255,255,0.05)",
            mt: 1,
            "& .MuiSvgIcon-root": { color: PALETTE.muted },
            "& .MuiTablePagination-select": { color: PALETTE.textPrimary },
          }}
        />
      </Box>

      <Snackbar
        open={Boolean(error)}
        autoHideDuration={8000}
        onClose={() => setError(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
      >
        <Alert severity="error" onClose={() => setError(null)} sx={{ width: "100%" }}>
          {error}
        </Alert>
      </Snackbar>
    </Box>
  );
};
